import React from 'react';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Language selector button with dropdown menu.
 *
 * v1-språk-strategi: only EN/DA active. DE/FR hibernating per v1.
 * Persists language choice in localStorage (backed by useLanguage hook
 * which syncs with backend).
 *
 * Button label is ISO code (uppercase 'EN', 'DA'). No flag emoji in v1
 * — design ethos: no icon, no emoji, typographic clarity. Accessibility
 * note: trigger label is just an ISO code, which is doctrine-correct but
 * invisible affordance to a non-Danish-speaking user landing on a Danish
 * UI; native title and aria-label provide a 'Change language' hint
 * without violating no-icon, no-emoji rule.
 */
export function LanguageSelector({ variant = 'default' }) {
  const { language, setLanguage, t } = useLanguage();
  const currentLang = LANGUAGES.find(l => l.code === language);
  const code = (currentLang?.code || 'en').toUpperCase();
  const tooltip = t('common.changeLanguage') || 'Change language';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'ghost' ? 'ghost' : 'outline'}
          size="sm"
          className="h-auto px-2 py-1 text-xs uppercase tracking-wider font-mono text-muted-foreground hover:text-foreground"
          data-testid="language-selector"
          title={tooltip}
          aria-label={`${tooltip} (${currentLang?.name || code})`}
        >
          {code}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-background border border-border rounded-none p-0 min-w-[180px]"
      >
        {LANGUAGES.map((lang) => {
          const isSelected = language === lang.code;
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={[
                'cursor-pointer rounded-none border-b border-border last:border-b-0',
                'grid grid-cols-[40px_1fr_24px] items-baseline gap-3 px-3 py-2.5',
                'text-xs transition-colors focus:bg-muted focus:text-foreground',
                isSelected
                  ? 'bg-foreground text-background focus:bg-foreground focus:text-background'
                  : 'text-foreground',
              ].join(' ')}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="font-mono uppercase tracking-wider">
                {lang.code.toUpperCase()}
              </span>
              <span>{lang.name}</span>
              <span className="font-mono text-right" aria-hidden="true">
                {isSelected ? '✓' : ''}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
